'use client'

import { isUndefined } from 'lodash-es'
import { useLocale, useTranslations } from 'next-intl'

import { LocaleSelector } from '@/features/change-locale'
import { Link, usePathname } from '@/shared/i18n'
import { useCommonDialog, useSession } from '@/shared/lib'
import { Button } from '@/shared/ui'
import { TextButton } from '@/shared/ui/text-button'

import { useBadges } from '../../lib/badges'
import { Layout } from '../layout'
import * as Navigation from '../navigation'
import { NotificationButton } from './notification-button'
import { ProfileButton } from './profile-button'

const PATH = {
  GUEST_MAIN: '/guest/main',
  HOST_MAIN: '/host/main',
} as const

type Props = {
  wide?: boolean
  isLoggedIn?: boolean
  isHost?: boolean
  userId?: number
  className?: string
}

type NavigationItem = {
  href: string
  label: string
  newBadge: boolean
  auth: boolean
  excludedPaths: string[]
}

export const NavigationBarUI = ({
  wide = false,
  isLoggedIn: isLoggedInProp = false,
  isHost: isHostProp = false,
  userId: userIdProp,
  className,
}: Props) => {
  const locale = useLocale()
  const t = useTranslations('navigation')

  const pathname = usePathname()
  const session = useSession()

  const isLoggedIn = isUndefined(session.data) ? isLoggedInProp : !!session.data
  const isHost = isUndefined(session.data)
    ? isHostProp
    : (session.data?.isHost ?? false)
  const isHostMode = !!pathname?.startsWith('/host')

  const { openMoveToSignInDialog } = useCommonDialog()

  const { badges } = useBadges({ isLoggedIn, userId: userIdProp })

  const guestNavigationItems: NavigationItem[] = [
    {
      href: '/guest/search',
      label: t('map'),
      newBadge: false,
      auth: false,
      excludedPaths: [],
    },
    {
      href: '/guest/contract',
      label: t('lease'),
      newBadge: badges.badgeGuestBooking > 0,
      auth: true,
      excludedPaths: [],
    },
    {
      href: '/guest/chat',
      label: t('chat'),
      newBadge: badges.badgeChat > 0,
      auth: true,
      excludedPaths: [],
    },
  ]

  const hostNavigationItems: NavigationItem[] = [
    {
      href: '/host/room',
      label: t('room'),
      newBadge: false,
      auth: true,
      excludedPaths: [
        '/host/room/*path/contract/*path',
        '/host/room/*path/preview',
      ],
    },
    {
      href: '/host/contract',
      label: t('lease'),
      newBadge: badges.badgeHostBooking > 0,
      auth: true,
      excludedPaths: [],
    },
    {
      href: '/host/chat',
      label: t('chat'),
      newBadge: badges.badgeChat > 0,
      auth: true,
      excludedPaths: [],
    },
  ]

  const navigationItems = isHostMode
    ? hostNavigationItems
    : guestNavigationItems

  const handleNotAccessible = (pathname: string) => {
    openMoveToSignInDialog(`/${locale}${pathname}`)
  }

  return (
    <Layout wide={wide} className={className}>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <Navigation.List>
          {navigationItems.map(
            ({ href, label, newBadge, auth, excludedPaths }) => (
              <Navigation.Item
                key={label}
                href={href}
                newBadge={newBadge}
                accessible={isLoggedIn || !auth}
                onNotAccessible={handleNotAccessible}
                excludedPaths={excludedPaths}
              >
                {label}
              </Navigation.Item>
            ),
          )}
        </Navigation.List>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-5">
        {(() => {
          if (!isHostMode && !isHost) {
            return (
              <TextButton asChild color="gray" size="small" weight="normal">
                <Link href={PATH.HOST_MAIN} locale="ko">
                  {t('start_to_landlord')}
                </Link>
              </TextButton>
            )
          }

          return (
            <TextButton color="gray" size="small" weight="normal" asChild>
              <Link
                href={isHostMode ? PATH.GUEST_MAIN : PATH.HOST_MAIN}
                locale="ko"
              >
                {isHostMode ? '임차인 모드로 전환' : t('switch_to_landlord')}
              </Link>
            </TextButton>
          )
        })()}
        {!isHostMode && <LocaleSelector isMobile={false} locale={locale} />}
        {isLoggedIn && (
          <NotificationButton
            href={isHostMode ? '/host/notification' : '/guest/notification'}
            hasNewNotification={
              isHostMode
                ? badges.badgeHostInbox > 0
                : badges.badgeGuestInbox > 0
            }
          />
        )}
        {!isLoggedIn && (
          <Button
            asChild
            variant="outline"
            color="primary"
            size="small"
            weight="medium"
          >
            <Link href="/sign-in">{t('auth')}</Link>
          </Button>
        )}
        <ProfileButton isHostMode={isHostMode} isLoggedIn={isLoggedIn} />
      </div>
    </Layout>
  )
}
